import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { filterService } from '@services/filterService';
import { STORAGE_KEYS } from '@lib/constants';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/Dialog';
import type { FilterOption } from '@/types/dashboard';
import './QuickFilters.css';

export interface QuickFilter {
  FieldName: string;
  Id: string;
  Name: string;
  IsCustomField: boolean;
  FieldTypeId: number | null;
  CustomFields: unknown[] | null;
  ComparisonOperator: string | null;
  isChipFilter: boolean | null;
  isSelected: boolean | null;
}

interface DashboardQuickFilters {
  DashboardId: number;
  Filters: QuickFilter[];
}

interface QuickFiltersProps {
  dashboardId: number | null;
  quickFiltersSelected: string[];
  onChangeQuickFiltersSelected: React.Dispatch<React.SetStateAction<string[]>>;
  configValueId?: number;
  onFilterChanged?: () => void;
  onFilterToggled?: (fieldName: string, isSelected: boolean) => void;
  onSyncReady?: (sync: QuickFiltersSyncAPI) => void;
  onSelectedFiltersChanged?: (filters: QuickFilter[]) => void;
}

export interface QuickFiltersSyncAPI {
  selectByFieldName: (fieldName: string, name: string) => void;
  unselectByFieldName: (fieldName: string, name: string) => void;
}

// Multi-value fields (AssignedTo, Status, ItemType...) can hold several options, so identity is FieldName + Id
const isSameFilter = (a: Pick<QuickFilter, 'FieldName' | 'Id'>, b: Pick<QuickFilter, 'FieldName' | 'Id'>) =>
  a.FieldName === b.FieldName && String(a.Id) === String(b.Id);

export const QuickFilters: React.FC<QuickFiltersProps> = ({
  dashboardId,
  quickFiltersSelected: _quickFiltersSelected,
  onChangeQuickFiltersSelected,
  configValueId = 1,
  onFilterChanged,
  onFilterToggled,
  onSyncReady,
  onSelectedFiltersChanged,
}) =>
{
  const { PersonId } = useAuth();
  const personId = PersonId();

  const [currentFilters, setCurrentFilters] = useState<QuickFilter[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSelectedFilter, setNewSelectedFilter] = useState<FilterOption | null>(null);
  const [availableFilters, setAvailableFilters] = useState<FilterOption[]>([]);
  const [filterSearch, setFilterSearch] = useState('');

  const defaultFilters = useMemo<QuickFilter[]>(
    () => [
      {
        FieldName: 'Me',
        Id: personId || '0',
        Name: 'ME',
        IsCustomField: false,
        FieldTypeId: null,
        CustomFields: null,
        ComparisonOperator: null,
        isChipFilter: null,
        isSelected: null,
      },
      {
        FieldName: 'IsRedFlag',
        Id: '1',
        Name: 'SHOW RED FLAG',
        IsCustomField: false,
        FieldTypeId: null,
        CustomFields: null,
        ComparisonOperator: null,
        isChipFilter: null,
        isSelected: null,
      },
      {
        FieldName: 'IsImportant',
        Id: '1',
        Name: 'SHOW IMPORTANT',
        IsCustomField: false,
        FieldTypeId: null,
        CustomFields: null,
        ComparisonOperator: null,
        isChipFilter: null,
        isSelected: null,
      },
      {
        FieldName: 'ShowCompleted',
        Id: '1',
        Name: 'SHOW COMPLETED',
        IsCustomField: false,
        FieldTypeId: null,
        CustomFields: null,
        ComparisonOperator: null,
        isChipFilter: null,
        isSelected: null,
      },
    ],
    [personId],
  );

  const getStoredFilters = useCallback((): DashboardQuickFilters[] | null =>
  {
    const stored = localStorage.getItem(STORAGE_KEYS.QUICK_FILTERS);
    return stored ? JSON.parse(stored) : null;
  }, []);

  const saveStoredFilters = useCallback((filters: DashboardQuickFilters[]) =>
  {
    localStorage.setItem(STORAGE_KEYS.QUICK_FILTERS, JSON.stringify(filters));
  }, []);

  const isDark = document.documentElement.classList.contains('dark');

  const loadQuickFilters = useCallback(() =>
  {
    if (!dashboardId) return;

    const savedArray = getStoredFilters();
    let savedFilters: QuickFilter[] | null = null;

    if (savedArray)
    {
      const dashboardEntry = savedArray.find(obj => obj.DashboardId === dashboardId);
      savedFilters = dashboardEntry?.Filters ?? null;
    }

    if (savedFilters)
    {
      setCurrentFilters(savedFilters);
      const selected = savedFilters.filter(f => f.isSelected);
      onChangeQuickFiltersSelected(selected.map(f => f.FieldName));
      onSelectedFiltersChanged?.(selected);
      onFilterChanged?.();
    }
    else
    {
      setCurrentFilters(defaultFilters);
      onChangeQuickFiltersSelected([]);
      onSelectedFiltersChanged?.([]);
    }
  }, [
    dashboardId,
    defaultFilters,
    getStoredFilters,
    onChangeQuickFiltersSelected,
    onFilterChanged,
    onSelectedFiltersChanged,
  ]);

  useEffect(() =>
  {
    loadQuickFilters();
  }, [loadQuickFilters]);

  const toggleFilterInStorage = useCallback(
    (filter: QuickFilter, forceSelected?: boolean) =>
    {
      if (!dashboardId) return;

      const filtersClone = structuredClone(defaultFilters);
      const localStorageFilters = getStoredFilters();

      if (!localStorageFilters)
      {
        const target = filtersClone.find(
          f => f.FieldName === filter.FieldName && f.Id === filter.Id,
        );
        if (target) target.isSelected = forceSelected ?? true;
        saveStoredFilters([{ DashboardId: dashboardId, Filters: filtersClone }]);
      }
      else
      {
        const dashboardEntry = localStorageFilters.find(obj => obj.DashboardId === dashboardId);

        if (!dashboardEntry?.Filters)
        {
          const target = filtersClone.find(
            f => f.FieldName === filter.FieldName && f.Id === filter.Id,
          );
          if (target) target.isSelected = forceSelected ?? true;
          localStorageFilters.push({ DashboardId: dashboardId, Filters: filtersClone });
          saveStoredFilters(localStorageFilters);
        }
        else
        {
          const currentFilter = dashboardEntry.Filters.find(
            f => f.FieldName === filter.FieldName && f.Id === filter.Id,
          );
          if (currentFilter)
          {
            currentFilter.isSelected = forceSelected ?? !currentFilter.isSelected;
            saveStoredFilters(localStorageFilters);
          }
        }
      }

      loadQuickFilters();
    },
    [dashboardId, defaultFilters, getStoredFilters, saveStoredFilters, loadQuickFilters],
  );

  // User clicks a QuickFilter chip directly
  const handleFilterClick = useCallback(
    (filter: QuickFilter) =>
    {
      if (!dashboardId) return;
      const newIsSelected = !filter.isSelected;
      toggleFilterInStorage(filter);
      onFilterToggled?.(filter.FieldName, newIsSelected);
    },
    [dashboardId, toggleFilterInStorage, onFilterToggled],
  );

  // Called externally by parent to sync from FilterBar
  const selectByFieldName = useCallback(
    (fieldName: string, name: string) =>
    {
      const match = currentFilters.find(
        f => f.FieldName === fieldName && f.Name.toUpperCase() === name.toUpperCase(),
      );
      if (match && !match.isSelected)
      {
        toggleFilterInStorage(match, true);
      }
    },
    [currentFilters, toggleFilterInStorage],
  );

  const unselectByFieldName = useCallback(
    (fieldName: string, name: string) =>
    {
      const match = currentFilters.find(
        f => f.FieldName === fieldName && f.Name.toUpperCase() === name.toUpperCase(),
      );
      if (match && match.isSelected)
      {
        toggleFilterInStorage(match, false);
      }
    },
    [currentFilters, toggleFilterInStorage],
  );

  // Expose sync functions to parent whenever they change
  useEffect(() =>
  {
    onSyncReady?.({ selectByFieldName, unselectByFieldName });
  }, [selectByFieldName, unselectByFieldName, onSyncReady]);

  const handleDeleteFilter = useCallback(
    (filter: QuickFilter) =>
    {
      if (!dashboardId) return;

      const localStorageFilters = getStoredFilters();
      if (!localStorageFilters) return;

      for (const item of localStorageFilters)
      {
        if (item.DashboardId === dashboardId)
        {
          const idx = item.Filters.findIndex(
            f => f.FieldName === filter.FieldName && f.Id === filter.Id,
          );
          if (idx !== -1)
          {
            item.Filters.splice(idx, 1);
          }
        }
      }

      saveStoredFilters(localStorageFilters);
      loadQuickFilters();
    },
    [dashboardId, getStoredFilters, saveStoredFilters, loadQuickFilters],
  );

  const fetchFilterOptions = useCallback(
    async (search: string) =>
    {
      try
      {
        const result = await filterService.getFilterOptions(search, {
          IsFromProject: true,
          ConfigValueId: configValueId,
        });
        setAvailableFilters(result);
      }
      catch
      {
        setAvailableFilters([]);
      }
    },
    [configValueId],
  );

  const handleOpenModal = useCallback(() =>
  {
    setIsModalOpen(true);
    setNewSelectedFilter(null);
    setFilterSearch('');
    fetchFilterOptions('');
  }, [fetchFilterOptions]);

  const handleSubmitFilter = useCallback(() =>
  {
    if (!newSelectedFilter || !dashboardId) return;

    const newFilter: QuickFilter = {
      FieldName: newSelectedFilter.FieldName,
      Id: newSelectedFilter.Id,
      Name: newSelectedFilter.Name,
      IsCustomField: newSelectedFilter.IsCustomField,
      FieldTypeId: newSelectedFilter.FieldTypeId,
      CustomFields: newSelectedFilter.CustomFields ?? null,
      ComparisonOperator: newSelectedFilter.ComparisonOperator ?? null,
      isChipFilter: null,
      isSelected: null,
    };

    const filtersClone = structuredClone(defaultFilters);
    const localStorageFilters = getStoredFilters();

    if (!localStorageFilters)
    {
      const alreadyExists = filtersClone.some(f => isSameFilter(f, newFilter));
      if (!alreadyExists)
      {
        filtersClone.push(newFilter);
      }
      saveStoredFilters([{ DashboardId: dashboardId, Filters: filtersClone }]);
    }
    else
    {
      const dashboardEntry = localStorageFilters.find(obj => obj.DashboardId === dashboardId);

      if (dashboardEntry?.Filters)
      {
        const alreadyExists = dashboardEntry.Filters.some(f => isSameFilter(f, newFilter));
        if (!alreadyExists)
        {
          dashboardEntry.Filters.push(newFilter);
          saveStoredFilters(localStorageFilters);
        }
      }
      else
      {
        const alreadyExists = filtersClone.some(f => isSameFilter(f, newFilter));
        if (!alreadyExists)
        {
          filtersClone.push(newFilter);
        }
        localStorageFilters.push({ DashboardId: dashboardId, Filters: filtersClone });
        saveStoredFilters(localStorageFilters);
      }
    }

    loadQuickFilters();
    setIsModalOpen(false);
    setNewSelectedFilter(null);
  }, [
    newSelectedFilter,
    dashboardId,
    defaultFilters,
    getStoredFilters,
    saveStoredFilters,
    loadQuickFilters,
  ]);

  return (
    <div className="quickfilters-component">
      <div className="quick-filters-container">
        <div className="qf-add-button" title="Add Quick Filters" onClick={handleOpenModal}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="12" fill={isDark ? '#3c6c80' : '#62aacb'} />
            <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="white" />
          </svg>
        </div>

        {currentFilters.map((filter, index) =>
          filter.Name ? (
            <div
              key={`${filter.FieldName}-${filter.Id}-${index}`}
              onClick={() => handleFilterClick(filter)}
              className={`qf-filter-button ${filter.isSelected ? 'selected' : ''}`}
            >
              {filter.Name}
              {filter.FieldName !== 'Me' && (
                <div
                  className="qf-delete-button"
                  title="Delete Quick Filter"
                  onClick={e =>
                  {
                    e.stopPropagation();
                    handleDeleteFilter(filter);
                  }}
                >
                  <X className="w-3 h-3" />
                </div>
              )}
            </div>
          ) : null,
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-[350px]">
          <DialogHeader>
            <DialogTitle>Select filter to add</DialogTitle>
          </DialogHeader>
          <div className="qf-modal-body">
            <input
              type="text"
              className="qf-filter-search"
              placeholder="Search filters..."
              value={filterSearch}
              onChange={e =>
              {
                setFilterSearch(e.target.value);
                fetchFilterOptions(e.target.value);
              }}
              autoFocus
            />
            {availableFilters.length > 0 && (
              <div className="qf-filter-options">
                {availableFilters
                  .filter(option => !currentFilters.some(f => isSameFilter(f, option)))
                  .map(option => (
                    <div
                      key={`${option.FieldName}-${option.Id}`}
                      className={`qf-filter-option ${
                        newSelectedFilter?.FieldName === option.FieldName &&
                        newSelectedFilter?.Id === option.Id
                          ? 'selected'
                          : ''
                      }`}
                      onClick={() => setNewSelectedFilter(option)}
                    >
                      {option.Name}
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-[#a9a093] bg-white dark:bg-[#3a3c3c] border border-gray-300 dark:border-[#4a4c4c] rounded-md hover:bg-gray-50 dark:hover:bg-[#4a4c4c]"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitFilter}
              disabled={!newSelectedFilter}
              className="px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50"
              style={{ backgroundColor: isDark ? '#3c6c80' : '#62aacb' }}
            >
              Add
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
